import { useRouter, useSegments } from 'expo-router';
import { useEffect, useMemo } from 'react';

import type { ReportResponseDto } from '@api/WhisApi/generated/model/reportResponseDto';
import type { UserDto } from '@api/WhisApi/generated/model/userDto';

import { canEditReport } from '@/utils/userPermissions';

interface UseReportEditPermissionGuardProps {
  isEditMode: boolean;
  reportId?: string;
  report?: ReportResponseDto | null;
  user?: UserDto | null;
}

export function useReportEditPermissionGuard({
  reportId,
  report,
  user,
  isEditMode,
}: UseReportEditPermissionGuardProps): boolean {
  const router = useRouter();
  const segments = useSegments() as string[];

  const isBoardRoute = useMemo(() => segments.includes('board'), [segments]);

  const reportDetailPathname = isBoardRoute
    ? '/(tabs)/board/reports/[reportId]'
    : '/(tabs)/reports/[reportId]';

  const hasEditPermission = canEditReport(report, user);

  useEffect(() => {
    if (!isEditMode || !reportId || !report || !user) {
      return;
    }

    if (hasEditPermission) {
      return;
    }

    router.replace({
      pathname: reportDetailPathname,
      params: { reportId },
    });
  }, [
    hasEditPermission,
    isEditMode,
    report,
    reportDetailPathname,
    reportId,
    router,
    user,
  ]);

  return hasEditPermission;
}
